/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, Form, Input, Modal, Tabs, Spin, message } from 'antd';
import React, { useEffect, useMemo, useState, useRef, useImperativeHandle } from 'react';
import dayjs from 'dayjs';
import { FormInfomation, FormInfomationRef } from './FormInfomation';
import TableHistory from './TableHistory';
import IntlMessages from '@/components/Locale/IntlMessages';
import { ApprovalNoticeBanner } from '@/components/common/ActionPlan/Modals/ApprovalNoticeBanner';
import { resolveCongViecApprovalNotices } from '@/components/common/ActionPlan/Modals/resolveCongViecApprovalNotices';
// import { PaperClipOutlined, SendOutlined } from '@ant-design/icons';
// import type { UploadFile } from 'antd';
import ReportTable from './ReportTable';
import TableBudget from './TableBudget';
import { useAuthStore } from '@/store/useAuthStore';
import { usePrivateStore } from '@/store/usePrivateStore';
import { congviecduanApi } from '@/services/action-plan/congviecduan';
// import { EditCongViecsRequest } from '@/types/action-plan/ActionPlan/EditCongViecsRequest';
// import { DuAnCongViecDetailView } from '@/types/action-plan/ActionPlan/EditCongViecsRequest';
import { DuAnCongViec } from '@/types/action-plan/ActionPlan/DuAnCongViec';
import { t } from 'i18next';
import { CongViecDuAnCheck, DuAnCongViecChiTietView, DuAnCongViecTree } from '@/types/action-plan/DuAnView';
import { DuAnCongViecDTO } from '@/types/action-plan/ActionPlan/DuAnCongViecDTO';
import { usePermission } from '@/hooks/usePermission';
import { canEditCongViecChiTiet } from '@/components/common/ActionPlan/utils/permissionUtils';
import { CONSTANTS } from '@/utils/constants';

export interface CongViecDetailBodyProps {
  /** Bật bind form / tabs (modal: theo `open`). */
  active?: boolean;
  handleclose: () => void;
  /** Sau lưu: gọi `handleclose` (modal). Trang task: false. */
  closeOnSave?: boolean;
  /** Nút góc tab bar (modal: Lưu + Đóng). */
  tabBarExtra?: React.ReactNode;
  /** Modal: hiển thị Lưu + Đóng trên tab bar. */
  showDefaultTabActions?: boolean;
  tabsClassName?: string;
  showFooter?: boolean;
  readOnly?: boolean;
  data?: DuAnCongViecChiTietView | null;
  loading?: boolean;
  listMucTieu?: any;
  idMucTieu?: string;
  idDuAn?: string;
  duan?: any;
  kanbans?: any[];
  onSaveSuccess?: (payload?: { listChange?: DuAnCongViecTree[] }) => void;
  onSaveNganSachSuccess?: () => void;
  initialActiveTab?: string;
  onActiveTabChange?: (key: string) => void;
  /** Trang task: tab Thảo luận tách khỏi Thông tin. */
  separateDiscussionTab?: boolean;
}

export interface CongViecDetailBodyRef {
  handleSave: () => Promise<void>;
  activeTab: string;
}

interface ModalFormDetailProps extends CongViecDetailBodyProps {
  open: boolean;
}

export const CongViecDetailBody = React.forwardRef<CongViecDetailBodyRef, CongViecDetailBodyProps>(
  (
    {
      active = true,
      handleclose,
      closeOnSave = true,
      tabBarExtra,
      showDefaultTabActions = false,
      tabsClassName = 'modal-detail-tabs',
      readOnly = false,
      data,
      loading,
      listMucTieu,
      idMucTieu,
      idDuAn,
      duan,
      kanbans = [],
      onSaveSuccess,
      onSaveNganSachSuccess,
      initialActiveTab,
      onActiveTabChange,
      separateDiscussionTab = false,
    },
    ref
  ) => {
    const isActive = active;
    const [form] = Form.useForm();
    const { profile } = useAuthStore();
    const getProfile = usePrivateStore((s) => s.getProfile);
    const idSelect = usePrivateStore((s) => s.getRequestHeader().selectTree?.idSelected || '');
    const [updating, setUpdating] = useState(false);
    const [rejectReason, setRejectReason] = useState<string>('');
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [approvalStateOverride, setApprovalStateOverride] = useState<{
      stateApproved: number;
      nguoiHoTroApproved?: string;
    } | null>(null);
    const [activeTab, setActiveTab] = useState<string>('1');

    useEffect(() => {
      if (isActive) {
        setActiveTab(initialActiveTab ?? '1');
      }
    }, [isActive, initialActiveTab]);

    useEffect(() => {
      setApprovalStateOverride(null);
    }, [data?.duAnCongViec?.congViec?.id, isActive]);

    const afterSaveSuccess = (payload?: { listChange?: DuAnCongViecTree[] }) => {
      onSaveSuccess?.(payload);
      if (closeOnSave) {
        handleclose();
      }
    };
    const [saving, setSaving] = useState(false);
    const formInfomationRef = useRef<FormInfomationRef>(null);

    const buoc8Permission = usePermission('buoc9'); // là bước action plan - buoc9 - trong sdk
    const canSaveByBuoc8 = Boolean(
      buoc8Permission?.update || buoc8Permission?.create || buoc8Permission?.fullcontrol
    );

    /** Chuẩn hóa select mục tiêu: API có thể trả `{ items }`, mảng phẳng `{ id, name }`, hoặc cây `{ objectives: [...] }`. */
    const filteredListMucTieu = useMemo(() => {
      const normalizedId =
        idMucTieu != null && String(idMucTieu) !== '' && String(idMucTieu) !== '0'
          ? String(idMucTieu)
          : '';

      const toRow = (obj: any, selectedId: string) => {
        const value = String(obj?.value ?? obj?.id ?? '');
        if (!value) return null;
        return {
          value,
          text: String(obj?.text ?? obj?.name ?? ''),
          disabled: Boolean(obj?.disabled),
          selected: selectedId ? value === selectedId : Boolean(obj?.selected),
        };
      };

      const collectFromItemsArray = (items: any[], selectedId: string) =>
        items.map((it) => toRow(it, selectedId)).filter(Boolean) as any[];

      const collectFromTreeArray = (arr: any[], selectedId: string) => {
        const out: any[] = [];
        for (const row of arr) {
          if (row?.objectives && Array.isArray(row.objectives)) {
            for (const obj of row.objectives) {
              const r = toRow(obj, selectedId);
              if (r) out.push(r);
            }
          } else {
            const r = toRow(row, selectedId);
            if (r) out.push(r);
          }
        }
        return out;
      };

      if (!listMucTieu) return listMucTieu;

      // Dạng { items: [...] }
      if (!Array.isArray(listMucTieu) && Array.isArray((listMucTieu as any).items)) {
        return { items: collectFromItemsArray((listMucTieu as any).items, normalizedId) };
      }

      if (!Array.isArray(listMucTieu)) return listMucTieu;

      // Không filter theo perspective khi chưa có id cụ thể
      if (!normalizedId) {
        return { items: collectFromTreeArray(listMucTieu, '') };
      }

      // Cây perspective: chỉ lấy objectives trong perspective chứa mục tiêu hiện tại
      for (const perspective of listMucTieu) {
        if (perspective?.objectives && Array.isArray(perspective.objectives)) {
          const found = perspective.objectives.find(
            (obj: any) => String(obj?.id ?? obj?.value ?? '') === normalizedId
          );
          if (found) {
            return {
              items: perspective.objectives
                .map((obj: any) => toRow(obj, normalizedId))
                .filter(Boolean) as any[],
            };
          }
        }
      }

      // Không tìm thấy perspective / hoặc list phẳng: trả full list đã chuẩn hóa
      return { items: collectFromTreeArray(listMucTieu, normalizedId) };
    }, [listMucTieu, idMucTieu]);
    const filteredSelectListDuAn = useMemo(() => {
      const selectListDuAn = data?.selectListDuAn as any;
      const currentValue = String(
        form.getFieldValue('duAn') ?? idDuAn ?? data?.duAnCongViec?.congViec?.duAnID ?? ''
      );

      const resolveCurrentDuAnLabel = (duAnId: string): string => {
        if (!duAnId) return '';
        const fromParent = data?.listCongViecParent?.[duAnId];
        if (fromParent) return String(fromParent);
        const fromCheck = data?.listCongViecDuAnCheck?.find(
          (c: CongViecDuAnCheck) => {
            if (String(c.id) !== duAnId) return false;
            // Root/dự án: không có cha, hoặc id chính là duAnID
            const parent = String(c.parentID ?? '').trim();
            return !parent || parent === '0' || String(c.duanID ?? '') === String(c.id);
          }
        );
        if (fromCheck?.tenCongViec) return String(fromCheck.tenCongViec);
        const anyCheck = data?.listCongViecDuAnCheck?.find(
          (c: CongViecDuAnCheck) => String(c.id) === duAnId
        );
        if (anyCheck?.tenCongViec) return String(anyCheck.tenCongViec);
        if (duan?.congViec?.id === duAnId && duan.congViec.tenCongViec) {
          return String(duan.congViec.tenCongViec);
        }
        const pickLabel = (item: any) => {
          const value = String(item?.value ?? item ?? '');
          if (value !== duAnId) return '';
          return String(item?.text ?? item?.label ?? item?.name ?? '');
        };
        if (Array.isArray(selectListDuAn)) {
          const found = selectListDuAn.map(pickLabel).find(Boolean);
          if (found) return found;
        } else if (Array.isArray(selectListDuAn?.items)) {
          const found = selectListDuAn.items.map(pickLabel).find(Boolean);
          if (found) return found;
        }
        return '';
      };

      const injectProjectLabel = (list: any): any => {
        const label = resolveCurrentDuAnLabel(currentValue);
        if (!currentValue || !label) return list;
        const row = { value: currentValue, text: label };
        if (Array.isArray(list)) {
          const idx = list.findIndex((i) => String(i?.value ?? i) === currentValue);
          if (idx >= 0) {
            const next = [...list];
            next[idx] = { ...next[idx], value: currentValue, text: label };
            return next;
          }
          return [...list, row];
        }
        const items = Array.isArray(list?.items) ? list.items : [];
        const idx = items.findIndex((i: any) => String(i?.value) === currentValue);
        if (idx >= 0) {
          const nextItems = [...items];
          nextItems[idx] = { ...nextItems[idx], value: currentValue, text: label };
          return { ...list, items: nextItems };
        }
        return { ...list, items: [...items, row] };
      };

      let allowedProjectIds = new Set<string>();
      let sessionProjects: Array<{ id?: string; name?: string }> = [];
      if (typeof window !== 'undefined') {
        try {
          const raw = window.sessionStorage.getItem(CONSTANTS.ACTION_PLAN.SESSION_KEY);
          if (raw) {
            const parsed = JSON.parse(raw);
            const sessionTrees = (parsed?.listDuAn ?? []) as DuAnCongViecTree[];
            sessionProjects = sessionTrees.map((p) => ({
              id: p.congViec?.id,
              name: p.congViec?.tenCongViec,
            }));
            allowedProjectIds = new Set(sessionProjects.map((p) => String(p?.id ?? '')).filter(Boolean));
          }
        } catch {
          // ignore malformed session
        }
      }

      // Nếu API không trả selectListDuAn (null/undefined) thì fallback build từ session.
      if (!selectListDuAn) {
        if (allowedProjectIds.size === 0) {
          const label = resolveCurrentDuAnLabel(currentValue);
          if (currentValue && label) {
            return { items: [{ value: currentValue, text: label }] };
          }
          return undefined;
        }
        return injectProjectLabel({
          items: sessionProjects
            .filter((p) => p?.id)
            .map((p) => ({ value: String(p.id), text: String(p.name ?? '') })),
        });
      }

      if (allowedProjectIds.size === 0) {
        return injectProjectLabel(selectListDuAn);
      }

      const keepId = (v: any) => {
        const id = String(v?.value ?? v ?? '');
        if (!id) return false;
        return allowedProjectIds.has(id) || (currentValue && id === currentValue);
      };

      if (Array.isArray(selectListDuAn)) {
        const filtered = selectListDuAn.filter(keepId);
        // Nếu filter ra rỗng thì fallback list session để Select (disabled) vẫn có option hiển thị.
        if (filtered.length === 0 && sessionProjects.length > 0) {
          return injectProjectLabel(
            sessionProjects
              .filter((p) => keepId({ value: p.id }))
              .map((p) => ({ value: String(p.id), text: String(p.name ?? '') }))
          );
        }
        return injectProjectLabel(filtered);
      }

      const items = Array.isArray(selectListDuAn?.items) ? selectListDuAn.items : [];
      const filteredItems = items.filter(keepId);
      if (filteredItems.length === 0 && sessionProjects.length > 0) {
        return injectProjectLabel({
          items: sessionProjects
            .filter((p) => keepId({ value: p.id }))
            .map((p) => ({ value: String(p.id), text: String(p.name ?? '') })),
        });
      }
      return injectProjectLabel({
        ...selectListDuAn,
        items: filteredItems,
      });
    }, [data?.selectListDuAn, data?.listCongViecParent, data?.listCongViecDuAnCheck, data?.duAnCongViec?.congViec?.duAnID, form, idDuAn, duan]);

    // Chuẩn hóa dữ liệu để bind form
    const mapped = useMemo(() => {
      if (!data) return { congViec: null, childs: [] as DuAnCongViecTree[], progress: 0 };
      // Hỗ trợ cả PascalCase và camelCase, ưu tiên duAnCongViec từ mock
      const duAnCongViec = data.duAnCongViec;
      let congViec: DuAnCongViecDTO | null = null;
      let childs: DuAnCongViecTree[] = [];
      let progress = 0;
      if (duAnCongViec) {
        congViec = duAnCongViec.congViec;
        childs = duAnCongViec.childs || [];
        progress = duAnCongViec.trongKy?.hoanThanh || 0;
      }
      // Tìm parent công việc từ ListCongViecDuAnCheck
      let parentCongViec: CongViecDuAnCheck | null = null;

      // Nếu có parentID, tìm parent theo parentID
      if (congViec?.level === 1) {
        // Nếu là level 1 hoặc không có parentID, tìm công việc có isDuAn = true
        // Ưu tiên: Tìm từ duan prop (dự án được truyền từ parent component)
        if (duan) {
          // duan có thể là DuAnCongViecTree hoặc có congViec bên trong
          if (duan.congViec && (duan.congViec.isDuAn === true || duan.congViec.ngayBatDau || duan.congViec.ngayKetThuc)) {
            parentCongViec = duan.congViec;
          } else if (duan.isDuAn === true || (duan.ngayBatDau && duan.ngayKetThuc)) {
            // duan chính là công việc có isDuAn = true
            parentCongViec = duan;
          }
        }

        // Nếu chưa tìm thấy từ duan prop, tìm trong data
        if (!parentCongViec) {
          const listCongViecDuAnCheck = data?.listCongViecDuAnCheck || [];
          // Tìm trong ListCongViecDuAnCheck
          const duAnCheck = listCongViecDuAnCheck.find((item: any) => item?.isDuAn === true);
          if (duAnCheck) {
            parentCongViec = duAnCheck;
          }
        }
      }

      return { congViec, childs, progress, parentCongViec };
    }, [data, duan]);

    useEffect(() => {
      if (!isActive) return;
      if (!mapped.congViec) return;

      const congViec: DuAnCongViec = mapped.congViec;
      const nvSupport =
        congViec?.nguoiHoTroID && typeof congViec.nguoiHoTroID === 'string'
          ? congViec.nguoiHoTroID.split(',').filter((id: string) => id && id.trim())
          : undefined;

      const datetime =
        congViec?.ngayBatDau && congViec?.ngayKetThuc
          ? [dayjs(congViec.ngayBatDau), dayjs(congViec.ngayKetThuc)]
          : undefined;

      const kanBanMucDoUuTien = congViec?.kanBanMucDoUuTien
        ? String(congViec.kanBanMucDoUuTien).toUpperCase()
        : 'LOW';


      const nganSachCongViec = data?.duAnCongViec.congViec.nganSach ?? 0;
      const phanTramNganSach = (data?.duAnCongViec.congViec.nganSachThucHien ?? 0) / (data?.duAnCongViec.congViec.nganSach ?? 0) * 100;
      const idMucTieuForm =
        congViec?.mtctid != null && String(congViec.mtctid).trim() !== ''
          ? String(congViec.mtctid)
          : idMucTieu != null && String(idMucTieu).trim() !== '' && String(idMucTieu) !== '0'
            ? String(idMucTieu)
            : undefined;

      const formValues = {
        projectName: congViec?.tenCongViec || '',
        nv: congViec?.nguoiPhuTrachID || undefined,
        nvSupport: nvSupport,
        nvNguoiTao: congViec?.nguoiTao ?? undefined,
        datetime,
        unit: congViec?.nganSach ?? congViec?.nganSachCal ?? 0,
        nganSachToolTip: nganSachCongViec > 0 ? `${data?.duAnCongViec.congViec.nganSachThucHien} / ${nganSachCongViec}` : `${nganSachCongViec}`,
        phanTramNganSach: phanTramNganSach,
        idMucTieu: idMucTieuForm,
        viTri: congViec?.viTri ?? 0,
        // Chỉ hiển thị: ưu tiên tiTrong, không thì tiTrongCal
        tiTrongHienThi: congViec?.tiTrong ?? congViec?.tiTrongCal ?? 0,

        // Tiến độ (hiển thị trong thanh progress)
        progress: mapped.progress || 0,
        duAn: idDuAn || congViec?.duAnID || undefined,
        description: congViec?.moTa ?? '',
        filesDinhKem: congViec?.filesDinhKem || undefined,
        kanBanMucDoUuTien: kanBanMucDoUuTien,
        hienThiTrangQuanLy: String(congViec?.id || '').startsWith('temp')
          ? true
          : (congViec?.hienThiTrangQuanLy ?? false),
      };
      form.setFieldsValue(formValues);
    }, [isActive, mapped, form, data, idDuAn, idMucTieu]);


    // Skeleton chỉ tắt khi có data
    const isLoading = !data;

    // Rule ver cũ ChiTiet.cshtml: phụ trách / hỗ trợ / chờ xác nhận / từ chối + nhận lại phụ trách
    const approvalNotices = useMemo(() => {
      if (!mapped.congViec || !profile) return [];
      const congViec = mapped.congViec;
      const userCurrent = getProfile();
      return resolveCongViecApprovalNotices({
        stateApproved: approvalStateOverride?.stateApproved ?? congViec.stateApproved ?? 0,
        nguoiPhuTrachID: congViec.nguoiPhuTrachID,
        nguoiTaoID: (congViec as { nguoiTao?: string }).nguoiTao,
        boPhanPhuTrachID: congViec.boPhanPhuTrachID,
        nguoiHoTroID: congViec.nguoiHoTroID || '',
        nguoiHoTroApproved:
          approvalStateOverride?.nguoiHoTroApproved ?? (congViec.nguoiHoTroApproved || ''),
        userID: String(profile.id),
        userBoPhanID: userCurrent?.boPhanId || idSelect || '',
        isQuanLy: Boolean(userCurrent?.isQuanLy),
        includeBoPhanRule: true,
        t,
      });
    }, [mapped.congViec, profile, getProfile, idSelect, t, approvalStateOverride]);

    const canEditTask = useMemo(() => {
      if (!data?.duAnCongViec?.congViec || !profile) return false;
      const congViec = data.duAnCongViec.congViec;
      return canEditCongViecChiTiet({
        roleItemTree: data.duAnCongViec.roleItemTree,
        stateApproved: approvalStateOverride?.stateApproved ?? congViec.stateApproved ?? 1,
        nguoiTaoID: (congViec as { nguoiTao?: string }).nguoiTao,
        userID: String(profile.id),
        checkFullConTroll: Boolean(data.checkFullConTroll),
      });
    }, [data, profile, approvalStateOverride]);

    const effectiveReadOnly = readOnly || !canEditTask;

    const actionNotice = useMemo(
      () =>
        approvalNotices.find(
          (n) => n.type === 'phuTrach' || n.type === 'boPhan' || n.type === 'hoTro' || n.type === 'reconfirm'
        ) ?? null,
      [approvalNotices]
    );

    // Xử lý đồng ý
    const handleApprove = async (isHoTro: boolean = false) => {
      if (!mapped.congViec || !idDuAn || !profile) return;

      setUpdating(true);
      try {
        const congViec = mapped.congViec;
        const idCongViec = congViec.id;
        if (!idCongViec) {
          message.error(t('bsc.msg.khongcoidcongviec', 'Không có ID công việc'));
          return;
        }
        if (isHoTro) {

          const response: any = await congviecduanApi.setHoTroApproved({
            userID: String(profile.id),
            id: idCongViec,
            state: 1,
            reason: '',
            phanHe: 'bsc',
          });
          const ok = response?.data?.success !== false && !response?.data?.error;
          if (ok) {
            message.success(t('bsc.msg.daxacnhandongy', 'Đã xác nhận đồng ý'));
            setApprovalStateOverride({
              stateApproved: congViec.stateApproved ?? 0,
              nguoiHoTroApproved: String(congViec.nguoiHoTroApproved || '')
                .split(',')
                .map((s) => s.trim())
                .filter((id) => id && id !== String(profile.id))
                .join(','),
            });
            onSaveSuccess?.();
          } else {
            message.error(response?.data?.message || t('bsc.msg.coloixayra', 'Có lỗi xảy ra'));
          }
        } else {
          // Cập nhật stateApproved = 1
          const response: any = await congviecduanApi.setStateApproved({
            id: idCongViec,
            state: 1,
            reason: '',
            phanHe: 'bsc',
          });
          const ok = response?.data?.success !== false && !response?.data?.error;
          if (ok) {
            message.success(t('bsc.msg.daxacnhandongy', 'Đã xác nhận đồng ý'));
            setApprovalStateOverride({ stateApproved: 1 });
            onSaveSuccess?.();
          } else {
            message.error(response?.data?.message || t('bsc.msg.coloixayra', 'Có lỗi xảy ra'));
          }
        }
      } catch (error) {
        console.error('Error approving:', error);
        message.error(t('bsc.msg.loikhiXacNhan', 'Có lỗi xảy ra khi xác nhận'));
      } finally {
        setUpdating(false);
      }
    };

    // Xử lý từ chối
    const handleReject = async (isHoTro: boolean = false) => {
      if (!mapped.congViec || !idDuAn || !profile) return;

      if (!rejectReason.trim()) {
        message.warning(t('bsc.msg.vuilongnhaplydotuchoi', 'Vui lòng nhập lý do từ chối'));
        return;
      }

      setUpdating(true);
      try {
        const congViec = mapped.congViec;
        const idCongViec = congViec.id;
        if (!idCongViec) {
          message.error(t('bsc.msg.khongcoidcongviec', 'Không có ID công việc'));
          return;
        }
        if (isHoTro) {

          const response: any = await congviecduanApi.setHoTroApproved({
            userID: String(profile.id),
            id: idCongViec,
            state: 2,
            reason: rejectReason.trim(),
            phanHe: 'bsc',
          });
          const ok = response?.data?.success !== false && !response?.data?.error;
          if (ok) {
            message.success(t('bsc.msg.datuchoi_thanhcong', 'Đã từ chối'));
            setShowRejectModal(false);
            setRejectReason('');
            setApprovalStateOverride({
              stateApproved: isHoTro ? (congViec.stateApproved ?? 0) : 2,
              nguoiHoTroApproved: isHoTro
                ? String(congViec.nguoiHoTroApproved || '')
                    .split(',')
                    .map((s) => s.trim())
                    .filter((id) => id && id !== String(profile.id))
                    .join(',')
                : congViec.nguoiHoTroApproved,
            });
            onSaveSuccess?.();
          } else {
            message.error(response?.data?.message || t('bsc.msg.coloixayra', 'Có lỗi xảy ra'));
          }
        } else {
          const response: any = await congviecduanApi.setStateApproved({
            id: idCongViec,
            state: 2,
            reason: rejectReason.trim(),
            phanHe: 'bsc',
          });
          const ok = response?.data?.success !== false && !response?.data?.error;
          if (ok) {
            message.success(t('bsc.msg.datuchoicongviec_thanhcong', 'Đã từ chối công việc'));
            setShowRejectModal(false);
            setRejectReason('');
            setApprovalStateOverride({ stateApproved: 2 });
            onSaveSuccess?.();
          } else {
            message.error(t('bsc.msg.coloixayra', 'Có lỗi xảy ra'));
          }
        }
      } catch (error) {
        console.error('Error rejecting:', error);
        message.error(t('bsc.msg.loikhiTuChoi', 'Có lỗi xảy ra khi từ chối'));
      } finally {
        setUpdating(false);
      }
    };

    // const handleSendDiscussion = async () => {
    //   if (!discussionValue.trim() || !mapped.congViec?.id) {
    //     message.warning('Vui lòng nhập nội dung thảo luận');
    //     return;
    //   }
    //   setSendingDiscussion(true);
    //   try {
    //     // TODO:  ====== Gửi thảo luận ======
    //     message.success('Đã gửi thảo luận');
    //     setDiscussionValue('');
    //   } catch (error: any) {
    //     console.error('Error sending discussion:', error);
    //     message.error(error?.response?.data?.message || 'Có lỗi xảy ra khi gửi thảo luận');
    //   } finally {
    //     setSendingDiscussion(false);
    //   }
    // };

    // const handleTriggerUpload = () => {
    //   const uploadElement = uploadRef.current;
    //   if (uploadElement) {
    //     const input = uploadElement.querySelector('input[type="file"]') as HTMLInputElement;
    //     if (input) {
    //       input.click();
    //     }
    //   }
    // };

    // const uploadProps = {
    //   multiple: true,
    //   fileList: discussionFiles,
    //   showUploadList: { showRemoveIcon: true },
    //   beforeUpload: () => false as any,
    //   onChange: (info: any) => {
    //     setDiscussionFiles(info.fileList || []);
    //   },
    //   onRemove: (file: UploadFile) => {
    //     setDiscussionFiles((prev) => prev.filter((f) => f.uid !== file.uid));
    //   },
    // };
    const handleSaveClick = async () => {
      if (formInfomationRef.current) {
        setSaving(true);
        try {
          await formInfomationRef.current.handleSave();
        } finally {
          setSaving(false);
        }
      }
    };

    useImperativeHandle(ref, () => ({
      handleSave: handleSaveClick,
      activeTab,
    }));

    const defaultTabBarExtra =
      showDefaultTabActions && !tabBarExtra ? (
        <div className="flex gap-2 mb-3">
          {!effectiveReadOnly && canSaveByBuoc8 && (
            <Button
              type="primary"
              hidden={activeTab !== '1'}
              onClick={() => void handleSaveClick()}
              loading={saving}
            >
              <IntlMessages id="btn.luu" text="Lưu" />
            </Button>
          )}
          <Button type="default" onClick={handleclose}>
            <IntlMessages id="btn.dong" text="Đóng" />
          </Button>
        </div>
      ) : (
        tabBarExtra
      );

    const detailBody = (
      <>
        {/* Banner xác nhận — ver cũ: từ chối + Đồng ý làm phụ trách chính */}
        {approvalNotices.map((notice) => (
          <ApprovalNoticeBanner
            key={notice.type}
            type={notice.type}
            message={notice.message}
            reason={notice.type === 'rejected' ? (data as { lyDoTuChoi?: string | null })?.lyDoTuChoi : undefined}
            updating={updating}
            t={t}
            onApprove={
              notice.type === 'phuTrach' ||
              notice.type === 'boPhan' ||
              notice.type === 'hoTro' ||
              notice.type === 'reconfirm'
                ? handleApprove
                : undefined
            }
            onReject={
              notice.type === 'phuTrach' || notice.type === 'boPhan' || notice.type === 'hoTro'
                ? () => setShowRejectModal(true)
                : undefined
            }
          />
        ))}
        <Form
          form={form}
          layout="vertical"
          onFinish={() => { }}
          className="flex-1 min-h-0 flex flex-col overflow-hidden"
        >
          {isLoading ? (
            <div className="flex-1 flex flex-col overflow-hidden" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Spin />
            </div>
          ) : (
            <>
              {/* Reject Reason Modal */}
              <Modal
                open={showRejectModal}
                title="Lý do từ chối"
                onOk={() => handleReject(actionNotice?.type === 'hoTro')}
                onCancel={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                okText="Xác nhận"
                cancelText="Hủy"
                confirmLoading={updating}
              >
                <Input.TextArea
                  rows={4}
                  placeholder="Nhập lý do từ chối..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                />
              </Modal>

              <Tabs
                activeKey={activeTab}
                onChange={(key) => {
                  setActiveTab(key);
                  onActiveTabChange?.(key);
                }}
                tabBarExtraContent={defaultTabBarExtra ? { right: defaultTabBarExtra } : undefined}
                className={`${tabsClassName} flex-1 flex flex-col overflow-hidden`}
                style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
                items={[
                  {
                    key: '1',
                    label: 'Thông tin',
                    children: (
                      <FormInfomation
                        ref={formInfomationRef}
                        form={form}
                        childs={mapped.childs}
                        progress={data?.duAnCongViec?.trongKy?.hoanThanh ?? 0}
                        selectListMucTieu={filteredListMucTieu}
                        selectListDuAn={filteredSelectListDuAn || undefined}
                        budgetPercentage={0}
                        sumNganSachChild={0}
                        isEdit={!effectiveReadOnly}
                        data={data}
                        loading={loading}
                        idDuAn={idDuAn}
                        parentCongViec={mapped.parentCongViec}
                        onSave={afterSaveSuccess}
                        onCancel={handleclose}
                        hideDiscussion={separateDiscussionTab}
                      />
                    ),
                  },
                  ...(separateDiscussionTab
                    ? [
                      {
                        key: '5',
                        label: 'Thảo luận',
                        children: (
                          <FormInfomation
                            form={form}
                            childs={mapped.childs}
                            data={data}
                            idDuAn={idDuAn}
                            parentCongViec={mapped.parentCongViec}
                            isEdit={true}
                            discussionOnly
                          />
                        ),
                      },
                    ]
                    : []),
                  {
                    key: '2',
                    label: 'Báo cáo',
                    children: (
                      <div className="h-full min-w-0 max-w-full overflow-x-hidden overflow-y-auto pr-1">
                        <ReportTable data={data} active={activeTab === '2'} onSaveSuccess={onSaveSuccess} readOnly={effectiveReadOnly} />
                      </div>
                    ),
                  },
                  {
                    key: '3',
                    label: 'Ngân sách',
                    children: <TableBudget data={data} kanbans={kanbans} onSaveNganSachSuccess={onSaveNganSachSuccess} />,
                  },
                  {
                    key: '4',
                    label: 'Lịch sử hoạt động',
                    children: (
                      <TableHistory
                        idCongViec={data?.duAnCongViec?.congViec?.id}
                        isDuAn={data?.duAnCongViec?.congViec?.isDuAn}

                      />
                    ),
                  },
                ]}
              />
              {React.createElement('style', {
                dangerouslySetInnerHTML: {
                  __html: `
               
                .modal-detail-wrap .ant-tabs-nav,
                .cong-viec-task-detail-page .ant-tabs-nav{
                  padding : 0px !important ;
                  margin : 0px !important ;

                }
                .modal-detail-wrap,
                .cong-viec-task-detail-page { 
                  overflow: hidden !important; 
                }
                  .modal-detail-wrap .ant-tabs-nav-wrap,
                  .cong-viec-task-detail-page .ant-tabs-nav-wrap{
                    padding : 12px !important ;
                    padding-top : 0px !important ;
                    padding-bottom : 6px !important ;
                    
                    font-weight: 600;
                  }
                  .modal-detail-wrap .ant-modal-header {
                    padding : 6px !important ;
                    padding-top : 0px !important ;
                  }
                .modal-detail-wrap .ant-modal-body,
                .cong-viec-task-detail-page { 
                  overflow: hidden !important; 
                  display: flex !important; 
                  flex-direction: column !important; 
                  height: 100% !important;
                  padding : 0px !important ;
                }
                .modal-detail-wrap .ant-modal-content { 
                  overflow: hidden;
                  display: flex; 
                  flex-direction: column; 
                  height: 100%;
                  max-height: 100vh;
                  padding : 14px !important ;
                  padding-right : 8px !important ;
                }
                .cong-viec-task-detail-page {
                  display: flex !important;
                  flex-direction: column !important;
                  flex: 1 !important;
                  min-height: 0 !important;
                  height: 100% !important;
                }
                .modal-detail-wrap .modal-detail-tabs,
                .cong-viec-task-detail-page .modal-detail-tabs { 
                  padding: 0 !important; 
                  height: 100% !important; 
                  display: flex !important; 
                  flex-direction: column !important; 
                  overflow: hidden !important;
                }
                .modal-detail-wrap .modal-detail-tabs .ant-tabs-nav,
                .cong-viec-task-detail-page .modal-detail-tabs .ant-tabs-nav { 
                  flex-shrink: 0 !important;
                }
                .modal-detail-wrap .modal-detail-tabs .ant-tabs-content-holder,
                .cong-viec-task-detail-page .modal-detail-tabs .ant-tabs-content-holder { 
                  border-radius: 16px;
                  flex: 1 !important; 
                  overflow: hidden; 
                  min-height: 0 !important;
                  min-width: 0 !important;
                  padding-top: 6px !important ;
                }
                .modal-detail-wrap .modal-detail-tabs .ant-tabs-content,
                .cong-viec-task-detail-page .modal-detail-tabs .ant-tabs-content { 
                  height: 100% !important;
                  min-width: 0 !important;
                  overflow-x: hidden !important;
                }
                .modal-detail-wrap .modal-detail-tabs .ant-tabs-tabpane,
                .cong-viec-task-detail-page .modal-detail-tabs .ant-tabs-tabpane { 
                  height: 100% !important; 
                  min-width: 0 !important;
                  overflow-y: auto !important;
                  overflow-x: hidden !important;
                }
                .modal-detail-wrap .modal-detail-tabs .ant-tabs-tabpane-active,
                .cong-viec-task-detail-page .modal-detail-tabs .ant-tabs-tabpane-active {
                  overflow-x: hidden !important;
                }
              `,
                },
              })}
            </>
          )}


        </Form>
      </>
    );

    return detailBody;
  }
);

CongViecDetailBody.displayName = 'CongViecDetailBody';

export const ModalFormDetail: React.FC<ModalFormDetailProps> = ({ open, handleclose, ...rest }) => (
  <Modal
    maskClosable={false}
    wrapClassName="modal-detail-wrap"
    title={t('title.congvieduan', 'Chi tiết công việc')}
    closeIcon={false}
    open={open}
    onCancel={handleclose}
    footer={null}
    width={750}
    destroyOnHidden
    styles={{
      header: { padding: 0, margin: 6, borderBottom: '1px dashed #e8e9ea' },
      body: { overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%', paddingTop: 0 },
      content: { overflow: 'hidden', maxHeight: '100vh', height: '100vh' },
    }}
    style={{ top: 0, bottom: 0, right: 0, left: 'auto', marginRight: 0, paddingBottom: 0 }}
    
  >
    <CongViecDetailBody
      active={open}
      handleclose={handleclose}
      closeOnSave
      showDefaultTabActions
      {...rest}
    />
  </Modal>
);
